import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CardListing from '../components/CardListing';
import LoadingSpinner from '../components/LoadingSpinner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';

export const MarketplacePage = () => {
  const { api, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watchedIds, setWatchedIds] = useState(new Set());
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    sport: searchParams.get('sport') || '',
    grade_company: searchParams.get('grade_company') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || ''
  });
  const [filterOpen, setFilterOpen] = useState(false);

  const sports = ['Baseball', 'Basketball', 'Football', 'Hockey', 'Soccer'];
  const gradeCompanies = ['PSA', 'BGS', 'SGC', 'Raw'];

  useEffect(() => {
    fetchListings();
  }, [searchParams]);

  // Fetch watchlist IDs when authenticated
  useEffect(() => {
    const fetchWatchlistIds = async () => {
      if (!isAuthenticated) {
        setWatchedIds(new Set());
        return;
      }
      try {
        const response = await api().get('/watchlist/ids');
        setWatchedIds(new Set(response.data.card_ids));
      } catch (error) {
        console.error('Failed to fetch watchlist:', error);
      }
    };
    fetchWatchlistIds();
  }, [isAuthenticated]);

  const handleWatchToggle = (cardId, isNowWatched) => {
    setWatchedIds(prev => {
      const newSet = new Set(prev);
      if (isNowWatched) {
        newSet.add(cardId);
      } else {
        newSet.delete(cardId);
      }
      return newSet;
    });
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('sport')) params.append('sport', searchParams.get('sport'));
      if (searchParams.get('grade_company')) params.append('grade_company', searchParams.get('grade_company'));
      if (searchParams.get('min_price')) params.append('min_price', searchParams.get('min_price'));
      if (searchParams.get('max_price')) params.append('max_price', searchParams.get('max_price'));

      const response = await api().get(`/cards?${params.toString()}`);
      setListings(response.data);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filters.sport) params.set('sport', filters.sport);
    if (filters.grade_company) params.set('grade_company', filters.grade_company);
    if (filters.min_price) params.set('min_price', filters.min_price);
    if (filters.max_price) params.set('max_price', filters.max_price);
    setSearchParams(params);
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setSearch('');
    setFilters({ sport: '', grade_company: '', min_price: '', max_price: '' });
    setSearchParams({});
  };

  const hasActiveFilters = search || filters.sport || filters.grade_company || filters.min_price || filters.max_price;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl uppercase text-white">Marketplace</h1>
          <p className="text-zinc-400 mt-2">{listings.length} cards available</p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="Search by player or set..."
              className="input-field pl-12"
              data-testid="marketplace-search"
            />
          </div>

          {/* Desktop Filters */}
          <div className="hidden md:flex items-center gap-3">
            <Select value={filters.sport} onValueChange={(v) => setFilters({ ...filters, sport: v })}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10" data-testid="filter-sport">
                <SelectValue placeholder="Sport" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border-white/10">
                {sports.map((sport) => (
                  <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.grade_company} onValueChange={(v) => setFilters({ ...filters, grade_company: v })}>
              <SelectTrigger className="w-32 bg-white/5 border-white/10" data-testid="filter-grade">
                <SelectValue placeholder="Grade" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border-white/10">
                {gradeCompanies.map((grade) => (
                  <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={applyFilters} className="btn-primary" data-testid="apply-filters">
              Search
            </Button>

            {hasActiveFilters && (
              <Button onClick={clearFilters} variant="ghost" className="text-zinc-400" data-testid="clear-filters">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Mobile Filters */}
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="outline" className="bg-white/5 border-white/10" data-testid="mobile-filters">
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                Filters
                {hasActiveFilters && <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full" />}
              </Button>
            </SheetTrigger>
            <SheetContent className="bg-[#09090B] border-white/10">
              <SheetHeader>
                <SheetTitle className="text-white">Filters</SheetTitle>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Sport</label>
                  <Select value={filters.sport} onValueChange={(v) => setFilters({ ...filters, sport: v })}>
                    <SelectTrigger className="w-full bg-white/5 border-white/10">
                      <SelectValue placeholder="All Sports" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      {sports.map((sport) => (
                        <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Grading Company</label>
                  <Select value={filters.grade_company} onValueChange={(v) => setFilters({ ...filters, grade_company: v })}>
                    <SelectTrigger className="w-full bg-white/5 border-white/10">
                      <SelectValue placeholder="All Grades" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-white/10">
                      {gradeCompanies.map((grade) => (
                        <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">Min Price</label>
                    <Input
                      type="number"
                      value={filters.min_price}
                      onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                      placeholder="$0"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">Max Price</label>
                    <Input
                      type="number"
                      value={filters.max_price}
                      onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                      placeholder="$999"
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={clearFilters} variant="outline" className="flex-1 bg-white/5 border-white/10">
                    Clear
                  </Button>
                  <Button onClick={applyFilters} className="flex-1 btn-primary">
                    Apply
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-400 text-lg">No cards found</p>
            {hasActiveFilters && (
              <Button onClick={clearFilters} variant="link" className="text-blue-400 mt-2">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <div className="card-grid" data-testid="listings-grid">
            {listings.map((card) => (
              <CardListing 
                key={card.id} 
                card={card} 
                isWatched={watchedIds.has(card.id)}
                onWatchToggle={handleWatchToggle}
              />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default MarketplacePage;
